<script setup>
import { ref, computed } from 'vue'
import SimpleCollapse from './SimpleCollapse.vue'

const props = defineProps({
  node: {
    type: Object,
    required: true
  },
  level: {
    type: Number,
    default: 0
  }
});

// Determine if this node should be collapsible
// Only show as collapsible if it has a title and is level 2 or higher
const isCollapsible = computed(() => {
  return props.node.title && props.node.level >= 2;
});

// Determine if this node has content to display
const hasContent = computed(() => {
  return props.node.html || (props.node.content && props.node.content.length > 0);
});

</script>

<template>
  <div class="QuickHelpNode">
    <!-- Collapsible section for h2+ headers -->
    <SimpleCollapse v-if="isCollapsible" :title="node.title">
      <!-- Node's own content -->
      <div v-if="node.html" class="NodeContent Markdown" v-html="node.html"></div>
      
      <!-- Recursively render children -->
      <div v-if="node.content && node.content.length > 0" class="ChildNodes">
        <QuickHelpNode 
          v-for="(child, index) in node.content" 
          :key="index"
          :node="child"
          :level="level + 1"
        />
      </div>
    </SimpleCollapse>
    
    <!-- Non-collapsible content (for root content or h1 headers) -->
    <div v-else-if="hasContent" class="NonCollapsibleNode">
      <!-- Show title as header if it exists and is h1 -->
      <h1 v-if="node.title && node.level === 1" class="NodeTitle">{{ node.title }}</h1>
      
      <!-- Node's own content -->
      <div v-if="node.html" class="NodeContent Markdown" v-html="node.html"></div>
      
      <!-- Recursively render children -->
      <div v-if="node.content && node.content.length > 0" class="ChildNodes">
        <QuickHelpNode 
          v-for="(child, index) in node.content" 
          :key="index"
          :node="child"
          :level="level + 1"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.QuickHelpNode {
  margin-bottom: var(--space-xs);
}

.NodeTitle {
  font-size: var(--f-xl);
  margin-bottom: var(--space-m);
}

.NodeContent {
  margin-bottom: var(--space-s);
}

.ChildNodes {
  margin-top: var(--space-s);
}

.NonCollapsibleNode .ChildNodes {
  margin-left: 0;
}

/* Markdown styling */
.Markdown:deep(h1) {
  font-size: var(--f-xl);
  margin-bottom: var(--space-m);
}

.Markdown:deep(h2) {
  font-size: var(--f-l);
  margin-top: var(--space-l);
  margin-bottom: var(--space-s);
}

.Markdown:deep(h3) {
  font-size: var(--f-m);
  margin-top: var(--space-m);
  margin-bottom: var(--space-s);
}

.Markdown:deep(p) {
  margin-bottom: var(--space-s);
}

.Markdown:deep(img) {
  max-width: 100%;
}

.Markdown:deep(table) {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid var(--b-20);
  margin-bottom: var(--space-s);
}

.Markdown:deep(th),
.Markdown:deep(td) {
  border: 1px solid var(--b-20);
  padding: var(--space-xxs);
}

.Markdown:deep(ul),
.Markdown:deep(ol) {
  margin-bottom: var(--space-s);
  padding-left: var(--space-l);
}

.Markdown:deep(li) {
  margin-bottom: var(--space-xxs);
}
</style>
